/**
 * Centralized PDF E-Ticket Generator
 * Modern design with consistent styling across all components
 */
import jsPDF from 'jspdf';

// Airport city names mapping
const airportCities = {
  'LHR': 'London Heathrow', 'LGW': 'London Gatwick', 'STN': 'London Stansted',
  'LTN': 'London Luton', 'MAN': 'Manchester', 'BHX': 'Birmingham',
  'DXB': 'Dubai', 'DOH': 'Doha', 'AUH': 'Abu Dhabi', 'JED': 'Jeddah',
  'RUH': 'Riyadh', 'MED': 'Medina', 'ISB': 'Islamabad', 'LHE': 'Lahore',
  'KHI': 'Karachi', 'DEL': 'New Delhi', 'BOM': 'Mumbai', 'JFK': 'New York JFK',
  'LAX': 'Los Angeles', 'ORD': 'Chicago', 'IST': 'Istanbul', 'CAI': 'Cairo',
  'AMM': 'Amman', 'KWI': 'Kuwait', 'BAH': 'Bahrain', 'MCT': 'Muscat',
  'ARN': 'Stockholm', 'OSL': 'Oslo', 'CPH': 'Copenhagen', 'HEL': 'Helsinki'
};

const getCityName = (code) => airportCities[code] || code;

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

const formatTimeDisplay = (timeStr) => {
  if (!timeStr) return '';
  const clean = timeStr.split('+')[0].split('Z')[0];
  return clean.substring(0, 5);
};

/**
 * Load company logo as base64
 */
const loadLogo = async () => {
  try {
    const response = await fetch('/logo-f380.png');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.log('Logo load failed');
    return null;
  }
};

/**
 * Collect all flight segments from booking data
 */
const collectAllSegments = (flight) => {
  let allSegments = [];
  const outboundSegments = flight.segments || [];
  const returnSegments = flight.return_segments || [];
  
  // Add outbound segments
  if (outboundSegments.length > 0) {
    allSegments = [...outboundSegments.map(s => ({...s, isReturn: false}))];
  } else if (flight.departure_date) {
    allSegments.push({
      marketing_airline: flight.airline_code || flight.airline || '',
      flight_number: (flight.flight_number || '').replace(/[A-Z]+/g, ''),
      departure_date: flight.departure_date,
      departure_airport: flight.from || flight.origin || '',
      arrival_airport: flight.to || flight.destination || '',
      departure_time: flight.departure_time || '',
      arrival_time: flight.arrival_time || '',
      booking_class: flight.booking_class || 'Y',
      isReturn: false
    });
  }
  
  // Add return segments
  if (returnSegments.length > 0) {
    allSegments = [...allSegments, ...returnSegments.map(s => ({...s, isReturn: true}))];
  } else if (flight.return_departure_date) {
    allSegments.push({
      marketing_airline: flight.return_airline_code || flight.airline_code || '',
      flight_number: (flight.return_flight_number || '').replace(/[A-Z]+/g, ''),
      departure_date: flight.return_departure_date,
      departure_airport: flight.to || flight.destination || '',
      arrival_airport: flight.from || flight.origin || '',
      departure_time: flight.return_departure_time || '',
      arrival_time: flight.return_arrival_time || '',
      booking_class: flight.return_booking_class || 'Y',
      isReturn: true
    });
  }
  
  return allSegments;
};

/**
 * Generate modern E-Ticket PDF
 * @param {Object} booking - Booking data object
 * @param {string} booking.pnr - Booking reference
 * @param {Array} booking.passengers - Array of passenger objects
 * @param {Object} booking.flight_data or booking.flight - Flight details
 * @param {number} booking.total_price or booking.price - Total price
 * @param {string} booking.status - Booking status
 */
export const generateETicketPDF = async (booking) => {
  const doc = new jsPDF();
  const pageWidth = 210;
  const marginLeft = 12;
  const marginRight = 198;
  const pageHeight = 297;
  const maxY = 270; // Maximum Y before footer area
  const flight = booking.flight_data || booking.flight || {};
  const passengers = booking.passengers || [];
  
  // Load company logo
  const logoBase64 = await loadLogo();
  
  // Helper function to check and add new page if needed
  const checkPageBreak = (requiredSpace) => {
    if (yPos + requiredSpace > maxY) {
      doc.addPage();
      yPos = 20;
      return true;
    }
    return false;
  };
  
  let yPos = 10;
  
  // === MODERN HEADER ===
  doc.setFillColor(15, 23, 42); // Dark slate
  doc.rect(0, 0, pageWidth, 45, 'F');
  
  // White rounded background for logo
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(marginLeft - 1, 7, 42, 16, 4, 4, 'F');
  
  // Add company logo
  if (logoBase64) {
    doc.addImage(logoBase64, 'PNG', marginLeft + 1, 9, 38, 12);
  } else {
    // Fallback text if logo fails to load
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(231, 49, 33);
    doc.text('Flight380', marginLeft + 4, 17);
  }
  
  // Header title
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(148, 163, 184); // Slate-400
  doc.text('E-TICKET / ITINERARY RECEIPT', pageWidth / 2, 16, { align: 'center' });
  
  // PNR box in header
  doc.setFillColor(30, 41, 59); // Slate-800
  doc.roundedRect(marginRight - 55, 8, 60, 28, 3, 3, 'F');
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text('BOOKING REFERENCE', marginRight - 25, 16, { align: 'center' });
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(56, 189, 248); // Sky-400
  doc.text(booking.pnr || 'N/A', marginRight - 25, 28, { align: 'center' });
  
  // Status badge
  doc.setFillColor(16, 185, 129); // Emerald-500
  doc.roundedRect(marginLeft, 32, 60, 8, 2, 2, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('CONFIRMED', marginLeft + 30, 37.5, { align: 'center' });
  
  yPos = 55;
  
  // === PASSENGER SECTION ===
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(71, 85, 105); // Slate-600
  doc.text('PASSENGERS', marginLeft, yPos);
  yPos += 6;
  
  // Passenger cards
  doc.setFillColor(248, 250, 252); // Slate-50
  const passengerBoxHeight = passengers.length * 8 + 6;
  doc.roundedRect(marginLeft, yPos, 186, passengerBoxHeight, 3, 3, 'F');
  
  yPos += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(30, 41, 59); // Slate-800
  
  passengers.forEach((p, idx) => {
    const lastName = (p.last_name || p.lastName || '').toUpperCase();
    const firstName = (p.first_name || p.firstName || '').toUpperCase();
    const title = (p.title || 'MR').toUpperCase();
    const paxType = (p.type || p.passenger_type || 'ADULT').toUpperCase();
    let typeLabel = 'Adult';
    if (paxType === 'CHILD' || paxType === 'CNN') typeLabel = 'Child';
    else if (paxType === 'INFANT' || paxType === 'INF') typeLabel = 'Infant';
    
    // Format: TITLE FIRSTNAME LASTNAME
    const fullName = `${title} ${firstName} ${lastName}`;
    
    doc.setFont('helvetica', 'bold');
    doc.text(`${idx + 1}.`, marginLeft + 4, yPos + 3);
    doc.text(fullName, marginLeft + 12, yPos + 3);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139); // Slate-500
    doc.text(`${title} • ${typeLabel}`, marginLeft + 100, yPos + 3);
    doc.setTextColor(30, 41, 59);
    yPos += 8;
  });
  
  yPos += 8;
  
  // === FLIGHT ITINERARY ===
  checkPageBreak(30);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(71, 85, 105);
  doc.text('FLIGHT ITINERARY', marginLeft, yPos);
  yPos += 6;
  
  // Collect ALL segments
  const allSegments = collectAllSegments(flight);
  
  // Draw each segment as a modern card
  let lastWasReturn = false;
  allSegments.forEach((seg, idx) => {
    // Check if we need a new page for this segment (each segment needs ~30px)
    checkPageBreak(35);
    
    // Add "Return Journey" label when switching
    if (seg.isReturn && !lastWasReturn && idx > 0) {
      yPos += 4;
      doc.setFillColor(100, 116, 139); // Slate-500
      doc.roundedRect(marginLeft, yPos, 40, 6, 1, 1, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('RETURN', marginLeft + 20, yPos + 4.5, { align: 'center' });
      yPos += 10;
    }
    lastWasReturn = seg.isReturn;
    
    const airline = (seg.marketing_airline || '').toUpperCase();
    const flightNum = (seg.flight_number || '').replace(/[A-Z]+/g, '');
    const depCode = (seg.departure_airport || '').toUpperCase();
    const arrCode = (seg.arrival_airport || '').toUpperCase();
    const depTime = formatTimeDisplay(seg.departure_time);
    const arrTime = formatTimeDisplay(seg.arrival_time);
    const depDate = formatDateDisplay(seg.departure_date);
    
    // Segment card
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(226, 232, 240); // Slate-200
    doc.roundedRect(marginLeft, yPos, 186, 22, 3, 3, 'FD');
    
    // Segment number badge
    doc.setFillColor(59, 130, 246); // Blue-500
    doc.roundedRect(marginLeft + 3, yPos + 3, 16, 16, 2, 2, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(`${idx + 1}`, marginLeft + 11, yPos + 13, { align: 'center' });
    
    // Flight info
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`${airline} ${flightNum}`, marginLeft + 24, yPos + 8);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(depDate, marginLeft + 24, yPos + 15);
    
    // Route with arrow
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(depCode, marginLeft + 85, yPos + 10);
    
    // Arrow
    doc.setFillColor(59, 130, 246);
    doc.setDrawColor(59, 130, 246);
    doc.line(marginLeft + 102, yPos + 8, marginLeft + 118, yPos + 8);
    doc.triangle(marginLeft + 118, yPos + 8, marginLeft + 114, yPos + 5, marginLeft + 114, yPos + 11, 'F');
    
    doc.text(arrCode, marginLeft + 122, yPos + 10);
    
    // Cities
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184);
    doc.text(getCityName(depCode), marginLeft + 85, yPos + 17);
    doc.text(getCityName(arrCode), marginLeft + 122, yPos + 17);
    
    // Times
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(depTime, marginLeft + 160, yPos + 8);
    doc.text(arrTime, marginLeft + 160, yPos + 17);
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184);
    doc.text('DEP', marginLeft + 180, yPos + 8);
    doc.text('ARR', marginLeft + 180, yPos + 17);
    
    yPos += 26;
  });
  
  yPos += 6;
  
  // === FARE BREAKDOWN ===
  checkPageBreak(45);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(71, 85, 105);
  doc.text('FARE BREAKDOWN', marginLeft, yPos);
  yPos += 6;
  
  const fareDetails = flight.fare_details || {};
  const totalPrice = booking.total_price || booking.price || flight.price || 0;
  const baseFare = fareDetails.base_fare || (totalPrice * 0.7).toFixed(2);
  const taxes = fareDetails.total_tax || (totalPrice * 0.3).toFixed(2);
  
  // Fare box
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(marginLeft, yPos, 186, 32, 3, 3, 'F');
  
  // Left side - breakdown
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text('Base Fare', marginLeft + 6, yPos + 10);
  doc.text('Taxes & Fees', marginLeft + 6, yPos + 18);
  
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text(`£${Number(baseFare).toFixed(2)}`, marginLeft + 50, yPos + 10);
  doc.text(`£${Number(taxes).toFixed(2)}`, marginLeft + 50, yPos + 18);
  
  // Divider
  doc.setDrawColor(226, 232, 240);
  doc.line(marginLeft + 90, yPos + 4, marginLeft + 90, yPos + 28);
  
  // Right side - total
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text('TOTAL', marginLeft + 100, yPos + 10);
  
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(16, 185, 129); // Emerald-500
  doc.text(`£${Number(totalPrice).toFixed(2)}`, marginLeft + 100, yPos + 24);
  
  // Baggage info
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text(`Baggage: ${fareDetails.baggage_pieces ? fareDetails.baggage_pieces + ' pieces' : '23kg included'}`, marginLeft + 145, yPos + 14);
  doc.text(`Class: ${fareDetails.fare_basis_code || 'Economy'}`, marginLeft + 145, yPos + 22);
  
  yPos += 40;
  
  // === IMPORTANT INFO - Always on new page ===
  doc.addPage();
  yPos = 20;
  
  doc.setFillColor(241, 245, 249); // Slate-100
  doc.roundedRect(marginLeft, yPos, 186, 28, 3, 3, 'F');
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(71, 85, 105);
  doc.text('IMPORTANT INFORMATION', marginLeft + 6, yPos + 6);
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(7);
  const notices = [
    '• Arrive at least 3 hours before international departure',
    '• Valid passport required (6+ months validity)',
    '• Check-in online 24-48 hours before departure',
    '• This is your electronic ticket - present ID at check-in'
  ];
  notices.forEach((notice, i) => {
    doc.text(notice, marginLeft + 6, yPos + 12 + (i * 4));
  });
  
  // === FOOTER ===
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 275, pageWidth, 22, 'F');
  
  // Footer logo text
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(231, 49, 33); // Brand red
  doc.text('Flight', marginLeft, 284);
  doc.setTextColor(26, 115, 232); // Brand blue
  doc.text('380', marginLeft + 15, 284);
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(148, 163, 184);
  doc.text('ATOL Protected • IATA Accredited', marginLeft + 35, 284);
  
  doc.text('www.flight380.co.uk | 01908 220000 / 01582 808050', marginRight - 10, 284, { align: 'right' });
  
  doc.setFontSize(6);
  doc.setTextColor(100, 116, 139);
  doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, marginLeft, 292);
  
  // === TERMS AND CONDITIONS PAGE ===
  doc.addPage();
  
  // Header
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageWidth, 25, 'F');
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('TERMS AND CONDITIONS', pageWidth / 2, 16, { align: 'center' });
  
  let tcY = 35;
  const lineHeight = 4.5;
  const sectionGap = 8;
  
  // Helper to add section
  const addSection = (title, content) => {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(title, marginLeft, tcY);
    tcY += 5;
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    
    content.forEach(line => {
      if (tcY > 275) {
        doc.addPage();
        tcY = 20;
      }
      doc.text(line, marginLeft, tcY, { maxWidth: 186 });
      tcY += lineHeight;
    });
    tcY += sectionGap;
  };
  
  // 1. Booking Confirmation
  addSection('1. BOOKING CONFIRMATION', [
    '• Your booking is confirmed upon receipt of full payment and issuance of this e-ticket.',
    '• Please verify all details including passenger names, flight dates, and times immediately.',
    '• Names must match exactly as they appear on your passport/travel document.',
    '• Any errors must be reported within 24 hours of booking confirmation.'
  ]);
  
  // 2. Payment Terms
  addSection('2. PAYMENT TERMS', [
    '• Full payment is required at the time of booking unless otherwise agreed.',
    '• All prices are quoted in British Pounds (GBP) and include applicable taxes.',
    '• Payment can be made by credit/debit card or bank transfer.',
    '• A processing fee may apply for certain payment methods.'
  ]);
  
  // 3. Changes and Amendments
  addSection('3. CHANGES AND AMENDMENTS', [
    '• Changes to bookings are subject to airline rules and availability.',
    '• Amendment fees may apply as per airline policy plus our service fee of £50 per passenger.',
    '• Name changes are generally not permitted after ticket issuance.',
    '• Date/route changes must be requested at least 72 hours before departure.',
    '• Any fare difference will be charged at the time of amendment.'
  ]);
  
  // 4. Cancellations and Refunds
  addSection('4. CANCELLATIONS AND REFUNDS', [
    '• Cancellation fees vary depending on the fare type and airline rules.',
    '• Non-refundable tickets cannot be refunded; only taxes may be recoverable.',
    '• Refund requests must be submitted in writing before the departure date.',
    '• Processing time for refunds is 8-12 weeks from the date of request.',
    '• A cancellation service fee of £75 per passenger applies to all refund requests.',
    '• No-shows forfeit the entire ticket value.'
  ]);
  
  // 5. Travel Documents
  addSection('5. TRAVEL DOCUMENTS AND VISAS', [
    '• Passengers are responsible for ensuring valid passports (minimum 6 months validity).',
    '• Visa requirements are the sole responsibility of the passenger.',
    '• Flight380 is not liable for denied boarding due to incorrect/missing travel documents.',
    '• We recommend checking entry requirements with the relevant embassy before travel.'
  ]);
  
  // 6. Check-in and Boarding
  addSection('6. CHECK-IN AND BOARDING', [
    '• Online check-in opens 24-48 hours before departure (varies by airline).',
    '• Arrive at the airport at least 3 hours before international departures.',
    '• Arrive at least 2 hours before domestic/short-haul departures.',
    '• Late arrival may result in denied boarding without refund.',
    '• Boarding gates close 30-45 minutes before scheduled departure.'
  ]);
  
  // 7. Baggage
  addSection('7. BAGGAGE ALLOWANCE', [
    '• Baggage allowance is determined by the airline and fare class purchased.',
    '• Excess baggage fees are payable directly to the airline at check-in.',
    '• Dangerous goods and prohibited items must not be packed in luggage.',
    '• Flight380 is not responsible for lost, damaged, or delayed baggage.',
    '• Claims for baggage issues must be made directly with the airline.'
  ]);
  
  // 8. Flight Changes by Airline
  addSection('8. SCHEDULE CHANGES AND CANCELLATIONS BY AIRLINE', [
    '• Airlines reserve the right to change flight times, routes, or cancel flights.',
    '• We will notify you of any changes as soon as we are informed.',
    '• In case of airline cancellation, we will assist with rebooking or refund options.',
    '• Flight380 is not liable for consequential losses due to airline changes.'
  ]);
  
  // 9. Liability
  addSection('9. LIABILITY', [
    '• Flight380 acts as an agent for airlines and is not liable for airline operations.',
    '• Our liability is limited to the cost of the ticket purchased.',
    '• We are not responsible for delays, cancellations, or service failures by airlines.',
    '• Travel insurance is strongly recommended for all bookings.',
    '• We are not liable for any indirect or consequential losses.'
  ]);
  
  // 10. Data Protection
  addSection('10. DATA PROTECTION', [
    '• Your personal data is processed in accordance with GDPR regulations.',
    '• Information is shared with airlines and authorities as required by law.',
    '• We do not sell your personal information to third parties.',
    '• For our full privacy policy, visit www.flight380.co.uk/privacy'
  ]);
  
  // 11. Complaints
  addSection('11. COMPLAINTS PROCEDURE', [
    '• Complaints should be submitted in writing within 28 days of travel.',
    '• Email: complaints@flight380.co.uk',
    '• We aim to respond to all complaints within 14 working days.',
    '• If unsatisfied, you may escalate to ABTA or relevant ombudsman service.'
  ]);
  
  // 12. Company Information
  addSection('12. COMPANY INFORMATION', [
    '• Flight380 is a trading name of Flight380 Ltd.',
    '• Registered Address: 277 Dunstable Road, Luton LU4 8BS, United Kingdom',
    '• ATOL License Number: Protected',
    '• IATA Accredited Agent',
    '• Contact: 01908 220000 / 01582 808050 | info@flight380.co.uk'
  ]);
  
  // Footer on T&C page
  doc.setFillColor(241, 245, 249);
  doc.rect(0, 280, pageWidth, 17, 'F');
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text('By completing this booking, you acknowledge that you have read, understood, and agree to these Terms and Conditions.', pageWidth / 2, 287, { align: 'center' });
  doc.text(`Booking Reference: ${booking.pnr || 'N/A'} | Document generated: ${new Date().toLocaleString('en-GB')}`, pageWidth / 2, 292, { align: 'center' });
  
  return doc;
};

/**
 * Generate and download E-Ticket PDF
 */
export const downloadETicketPDF = async (booking) => {
  const doc = await generateETicketPDF(booking);
  doc.save(`Flight380_${booking.pnr || 'ETicket'}.pdf`);
};

/**
 * Generate Invoice PDF
 */
export const generateInvoicePDF = async (booking) => {
  const doc = new jsPDF();
  const flight = booking.flight_data || booking.flight || {};
  const passengers = booking.passengers || [];
  const contact = booking.contact || {};
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.split('+')[0].substring(0, 5);
  };
  
  // Header
  doc.setFillColor(231, 49, 33);
  doc.rect(0, 0, 210, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', 20, 20);
  doc.setFontSize(12);
  doc.text('Flight380', 20, 30);
  
  // Invoice details
  doc.setFontSize(10);
  doc.text(`Invoice #: INV-${booking.pnr}`, 140, 15);
  doc.text(`Date: ${formatDate(booking.created_at)}`, 140, 22);
  doc.text(`PNR: ${booking.pnr}`, 140, 29);
  
  let yPos = 55;
  
  // Bill To section
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Bill To:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  yPos += 8;
  
  const customerName = passengers[0] 
    ? `${passengers[0].title || ''} ${passengers[0].first_name || ''} ${passengers[0].last_name || ''}`.trim()
    : 'Customer';
  doc.text(customerName, 20, yPos);
  yPos += 6;
  doc.text(contact.email || '', 20, yPos);
  yPos += 6;
  doc.text(contact.phone || '', 20, yPos);
  yPos += 6;
  if (contact.address) {
    doc.text(`${contact.address}, ${contact.city || ''}, ${contact.country || ''}`, 20, yPos);
    yPos += 6;
  }
  
  yPos += 10;
  
  // Flight Details Table Header
  doc.setFillColor(240, 240, 240);
  doc.rect(15, yPos, 180, 10, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Description', 20, yPos + 7);
  doc.text('Details', 100, yPos + 7);
  doc.text('Amount', 165, yPos + 7);
  yPos += 15;
  
  // Flight row
  doc.setFont('helvetica', 'normal');
  const flightRoute = `${flight.from || flight.origin || 'N/A'} → ${flight.to || flight.destination || 'N/A'}`;
  doc.text('Flight Booking', 20, yPos);
  doc.text(flightRoute, 100, yPos);
  yPos += 6;
  
  // Outbound details
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`Outbound: ${formatDate(flight.departure_date)} at ${formatTime(flight.departure_time)}`, 20, yPos);
  doc.text(`${flight.airline || ''} ${flight.flight_number || ''}`, 100, yPos);
  yPos += 5;
  
  // Return flight if exists
  if (flight.return_departure_time) {
    doc.text(`Return: ${formatDate(flight.return_departure_date)} at ${formatTime(flight.return_departure_time)}`, 20, yPos);
    yPos += 5;
  }
  
  // Passengers
  yPos += 5;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.text(`Passengers: ${passengers.length}`, 20, yPos);
  passengers.forEach((p, i) => {
    yPos += 5;
    doc.setFontSize(9);
    doc.text(`  ${i + 1}. ${p.title || ''} ${p.first_name || ''} ${p.last_name || ''} (${p.type || 'ADULT'})`, 25, yPos);
  });
  
  yPos += 15;
  
  // Total
  doc.setDrawColor(200, 200, 200);
  doc.line(15, yPos, 195, yPos);
  yPos += 10;
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Total Amount:', 120, yPos);
  doc.setTextColor(231, 49, 33);
  doc.text(`£${(booking.total_price || 0).toFixed(2)}`, 165, yPos);
  
  yPos += 15;
  
  // Payment Status
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  const status = booking.status || 'CONFIRMED';
  doc.text('Payment Status:', 120, yPos);
  if (status === 'CONFIRMED' || status === 'PAID') {
    doc.setTextColor(34, 197, 94);
    doc.text('PAID', 165, yPos);
  } else {
    doc.setTextColor(245, 158, 11);
    doc.text('PENDING', 165, yPos);
  }
  
  // Footer
  doc.setTextColor(128, 128, 128);
  doc.setFontSize(9);
  doc.text('Flight380 | www.flight380.co.uk | info@flight380.co.uk', 105, 280, { align: 'center' });
  doc.text('Thank you for your business!', 105, 286, { align: 'center' });
  
  return doc;
};

/**
 * Generate and download Invoice PDF
 */
export const downloadInvoicePDF = async (booking) => {
  const doc = await generateInvoicePDF(booking);
  doc.save(`Invoice_${booking.pnr || 'Invoice'}.pdf`);
};

/**
 * Generate HTML content for printing
 */
export const generatePrintHTML = (booking) => {
  const flight = booking.flight_data || booking.flight || {};
  const passengers = booking.passengers || [];
  
  const formatTime = (time) => {
    if (!time) return '';
    const match = time.match(/^(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : time;
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { 
        weekday: 'short', 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };
  
  const passengersHtml = passengers.map((p, idx) => `
    <tr>
      <td style="padding: 10px; border-bottom: 1px solid #eee;">${idx + 1}</td>
      <td style="padding: 10px; border-bottom: 1px solid #eee; font-weight: 600;">${(p.title || 'MR').toUpperCase()} ${(p.first_name || p.firstName || '').toUpperCase()} ${(p.last_name || p.lastName || '').toUpperCase()}</td>
      <td style="padding: 10px; border-bottom: 1px solid #eee;">${p.type || 'ADULT'}</td>
      <td style="padding: 10px; border-bottom: 1px solid #eee; color: #10b981;">E-TICKET</td>
    </tr>
  `).join('');
  
  // Collect all segments for printing
  const allSegments = collectAllSegments(flight);
  
  const segmentsHtml = allSegments.map((seg, idx) => `
    <div style="background: #f8fafc; border-radius: 8px; padding: 16px; margin-bottom: 12px; border-left: 4px solid ${seg.isReturn ? '#3b82f6' : '#E73121'};">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
        <div style="display: flex; align-items: center; gap: 12px;">
          <span style="background: #3b82f6; color: white; width: 28px; height: 28px; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-weight: bold;">${idx + 1}</span>
          <div>
            <div style="font-weight: bold; font-size: 14px;">${(seg.marketing_airline || '').toUpperCase()} ${(seg.flight_number || '').replace(/[A-Z]+/g, '')}</div>
            <div style="color: #64748b; font-size: 12px;">${formatDateDisplay(seg.departure_date)}</div>
          </div>
        </div>
        ${seg.isReturn ? '<span style="background: #3b82f6; color: white; padding: 4px 8px; border-radius: 4px; font-size: 11px;">RETURN</span>' : ''}
      </div>
      <div style="display: flex; justify-content: space-between; align-items: center;">
        <div style="text-align: center;">
          <div style="font-size: 20px; font-weight: bold;">${formatTime(seg.departure_time)}</div>
          <div style="font-size: 16px; font-weight: 600; color: #E73121;">${(seg.departure_airport || '').toUpperCase()}</div>
          <div style="font-size: 11px; color: #64748b;">${getCityName(seg.departure_airport)}</div>
        </div>
        <div style="text-align: center; flex: 1; padding: 0 20px;">
          <div style="height: 2px; background: linear-gradient(to right, #E73121, #10b981); margin: 8px 0;"></div>
          <div style="font-size: 18px;">✈</div>
        </div>
        <div style="text-align: center;">
          <div style="font-size: 20px; font-weight: bold;">${formatTime(seg.arrival_time)}</div>
          <div style="font-size: 16px; font-weight: 600; color: #E73121;">${(seg.arrival_airport || '').toUpperCase()}</div>
          <div style="font-size: 11px; color: #64748b;">${getCityName(seg.arrival_airport)}</div>
        </div>
      </div>
    </div>
  `).join('');
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>E-Ticket - ${booking.pnr}</title>
      <style>
        body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 20px; background: #f5f5f5; }
        .container { max-width: 800px; margin: 0 auto; background: white; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
        .header { background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); color: white; padding: 25px; display: flex; justify-content: space-between; align-items: center; }
        .logo { background: white; padding: 8px 16px; border-radius: 8px; display: inline-flex; align-items: center; gap: 4px; }
        .logo-flight { font-size: 20px; font-weight: bold; color: #E73121; }
        .logo-380 { font-size: 20px; font-weight: bold; color: #1a73e8; }
        .pnr-box { background: #1e293b; color: white; padding: 20px; display: flex; justify-content: space-between; align-items: center; }
        .pnr-value { font-size: 28px; font-weight: bold; color: #38bdf8; letter-spacing: 3px; }
        .section { padding: 20px; border-bottom: 1px solid #eee; }
        .section-title { color: #64748b; font-weight: 600; margin-bottom: 15px; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; }
        .price-box { background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); color: white; padding: 20px; text-align: center; }
        .price { font-size: 32px; font-weight: bold; color: #10b981; }
        .footer { padding: 15px; text-align: center; background: #f8f9fa; font-size: 12px; color: #666; }
        @media print { body { padding: 0; background: white; } .container { box-shadow: none; } }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <div class="logo">
            <span style="font-size: 18px; margin-right: 4px;">✈</span>
            <span class="logo-flight">Flight</span><span class="logo-380">380</span>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 14px; color: #94a3b8;">E-TICKET ITINERARY</div>
            <div style="font-size: 11px; opacity: 0.7;">Generated: ${new Date().toLocaleDateString('en-GB')}</div>
          </div>
        </div>
        
        <div class="pnr-box">
          <div>
            <div style="font-size: 10px; color: #94a3b8; text-transform: uppercase;">Booking Reference</div>
            <div class="pnr-value">${booking.pnr}</div>
          </div>
          <div style="text-align: right;">
            <div style="background: #10b981; color: white; padding: 6px 16px; border-radius: 20px; font-weight: 600; font-size: 12px;">✓ ${booking.status || 'CONFIRMED'}</div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Passenger Details</div>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background: #f8f9fa; font-size: 11px; color: #666; text-transform: uppercase;">
                <th style="padding: 10px; text-align: left;">#</th>
                <th style="padding: 10px; text-align: left;">Name</th>
                <th style="padding: 10px; text-align: left;">Type</th>
                <th style="padding: 10px; text-align: left;">Ticket</th>
              </tr>
            </thead>
            <tbody>${passengersHtml}</tbody>
          </table>
        </div>
        
        <div class="section">
          <div class="section-title">Flight Itinerary</div>
          ${segmentsHtml}
        </div>
        
        <div class="price-box">
          <div style="font-size: 11px; color: #94a3b8;">Total Amount</div>
          <div class="price">£${(booking.total_price || booking.price || 0).toFixed(2)}</div>
          <div style="font-size: 12px; color: #64748b; margin-top: 4px;">${passengers.length} Passenger(s) | All taxes included</div>
        </div>
        
        <div class="footer">
          <div style="font-weight: 600;"><span style="color: #E73121;">Flight</span><span style="color: #1a73e8;">380</span> - Your Travel Partner</div>
          <div>www.flight380.co.uk | support@flight380.co.uk</div>
          <div style="margin-top: 10px; padding: 10px; background: #fef3c7; border-radius: 4px; color: #92400e;">
            Please arrive at the airport at least 3 hours before international departures.
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Open print dialog with e-ticket
 */
export const printETicket = (booking) => {
  const printWindow = window.open('', '_blank');
  const content = generatePrintHTML(booking);
  printWindow.document.write(content);
  printWindow.document.close();
  printWindow.print();
};
